import { useState } from 'react'
import { CATEGORIAS_MODELO } from '../../data/modelos'
import { Search, Check, ChevronDown, X, PencilLine } from 'lucide-react'

const TODOS_MODELOS = Object.entries(CATEGORIAS_MODELO).flatMap(([cat, modelos]) =>
  modelos.map(m => ({ cat, m }))
)

/**
 * Combobox de modelo com busca + accordion por categoria
 * Props:
 *   value            — modelo selecionado (string)
 *   onChange         — callback(modelo: string)
 *   compact          — modo compacto (só busca, sem accordion visível por padrão)
 *   isPersonalizado  — true quando está em modo texto livre
 *   onPersonalizado  — callback para ativar modo personalizado
 */
export function ModeloCombobox({ value, onChange, compact = false, isPersonalizado = false, onPersonalizado }) {
  const [busca, setBusca] = useState('')
  const [catAberta, setCatAberta] = useState(null)
  const [accordionAberto, setAccordionAberto] = useState(false)

  const filtrados = busca.trim().length >= 1
    ? TODOS_MODELOS.filter(({ m }) => m.toLowerCase().includes(busca.trim().toLowerCase()))
    : []

  // Modelo já selecionado (não-personalizado)
  if (value && !isPersonalizado) {
    return (
      <div className="flex items-center gap-2 px-3 py-2 bg-[#111111]/5 border border-[#111111]/20 rounded-lg">
        <Check size={13} className="text-[#111111] shrink-0" />
        <span className="flex-1 text-sm font-semibold text-[#111111]">{value}</span>
        <button
          type="button"
          onClick={() => { onChange(''); setBusca(''); setAccordionAberto(false) }}
          className="text-xs text-slate-400 hover:text-red-500 transition-colors"
        >
          trocar
        </button>
      </div>
    )
  }

  return (
    <div className="space-y-1.5">
      {/* Campo de busca */}
      <div className="relative">
        <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
        <input
          type="text"
          value={busca}
          onChange={e => { setBusca(e.target.value); if (compact) setAccordionAberto(false) }}
          placeholder="Buscar modelo..."
          className="input pl-8 pr-8 text-sm"
          autoComplete="off"
        />
        {busca && (
          <button
            type="button"
            onClick={() => setBusca('')}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
          >
            <X size={13} />
          </button>
        )}
      </div>

      {/* Resultados da busca */}
      {busca.trim() ? (
        filtrados.length > 0 ? (
          <div className="border border-slate-200 rounded-xl overflow-hidden max-h-52 overflow-y-auto">
            {filtrados.map(({ cat, m }) => (
              <button
                key={m}
                type="button"
                onClick={() => { onChange(m); setBusca('') }}
                className="w-full flex items-center justify-between px-4 py-2 text-sm text-slate-700 hover:bg-[#F5C200]/10 hover:text-[#111111] transition-colors border-b border-slate-50 last:border-0"
              >
                <span>{m}</span>
                <span className="text-[10px] text-slate-400 shrink-0 ml-2">{cat}</span>
              </button>
            ))}
          </div>
        ) : (
          <p className="text-xs text-slate-400 px-1">Nenhum modelo para "{busca}"</p>
        )
      ) : compact ? (
        /* Modo compacto: accordion só abre ao clicar */
        <>
          <button
            type="button"
            onClick={() => setAccordionAberto(v => !v)}
            className="w-full flex items-center justify-between px-3 py-1.5 text-xs text-slate-500 hover:text-slate-700 bg-slate-50 hover:bg-slate-100 border border-slate-200 rounded-lg transition-colors"
          >
            <span>ou selecione por categoria</span>
            <ChevronDown size={12} className={`transition-transform ${accordionAberto ? 'rotate-180' : ''}`} />
          </button>

          {accordionAberto && (
            <div className="border border-slate-200 rounded-xl overflow-hidden">
              {Object.entries(CATEGORIAS_MODELO).map(([cat, modelos]) => (
                <div key={cat} className="border-b border-slate-100 last:border-0">
                  <button
                    type="button"
                    onClick={() => setCatAberta(catAberta === cat ? null : cat)}
                    className="w-full flex items-center justify-between px-3 py-2 bg-slate-50 hover:bg-[#FFFDE7] text-xs font-semibold text-slate-700 transition-colors"
                  >
                    <span>{cat}</span>
                    <ChevronDown size={12} className={`transition-transform text-slate-400 ${catAberta === cat ? 'rotate-180' : ''}`} />
                  </button>
                  {catAberta === cat && (
                    <div className="grid grid-cols-2 gap-1 p-1.5 bg-white">
                      {modelos.map(m => (
                        <button
                          key={m}
                          type="button"
                          onClick={() => { onChange(m); setCatAberta(null); setAccordionAberto(false) }}
                          className="text-left px-2 py-1 text-xs text-slate-700 hover:bg-[#F5C200]/10 hover:text-[#111111] rounded-lg transition-colors"
                        >
                          {m}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </>
      ) : (
        /* Modo completo: accordion sempre visível */
        <div className="border border-slate-200 rounded-xl overflow-hidden">
          {Object.entries(CATEGORIAS_MODELO).map(([cat, modelos]) => (
            <div key={cat} className="border-b border-slate-100 last:border-0">
              <button
                type="button"
                onClick={() => setCatAberta(catAberta === cat ? null : cat)}
                className="w-full flex items-center justify-between px-4 py-2.5 bg-slate-50 hover:bg-[#FFFDE7] text-sm font-semibold text-slate-700 transition-colors"
              >
                <span>{cat}</span>
                <ChevronDown size={14} className={`transition-transform text-slate-400 ${catAberta === cat ? 'rotate-180' : ''}`} />
              </button>
              {catAberta === cat && (
                <div className="grid grid-cols-2 gap-1 p-2 bg-white">
                  {modelos.map(m => (
                    <button
                      key={m}
                      type="button"
                      onClick={() => { onChange(m); setCatAberta(null) }}
                      className="text-left px-3 py-1.5 text-sm text-slate-700 hover:bg-[#F5C200]/10 hover:text-[#111111] rounded-lg transition-colors"
                    >
                      {m}
                    </button>
                  ))}
                </div>
              )}
            </div>
          ))}

          {onPersonalizado && (
            <button
              type="button"
              onClick={onPersonalizado}
              className="w-full flex items-center gap-2 px-4 py-3 bg-[#FFFDE7] hover:bg-[#FFF9C2] text-sm font-semibold text-[#CC8800] transition-colors border-t border-slate-100"
            >
              <PencilLine size={14} />
              Móvel personalizado (descrição livre)
            </button>
          )}
        </div>
      )}
    </div>
  )
}
